import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
    SuiModal,
    SuiModalHeader,
    SuiModalContent,
    SuiModalFooter,
    SuiButton,
    SuiContainer,
    SuiTextInput,
    SuiStateButton,
} from '@splashthat/sui';
import { SplashAppTitle } from 'common/components/Typography';
import { addFormToLibraryEffect } from '../../epics/formLibraryEffects';
import { getFormLibraryError, getFormLibraryStatus } from '../../selectors/formLibrary';
import { SAVED, SAVING } from '../../reducers/formLibrary';
import { setFormLibraryError } from '../../actions/formLibrary';

const ModalContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ModalDescription = styled.div`
    margin: 1rem 5rem;
    text-align: center;
`;

export const FORM_NAME_ERROR_MESSAGE = 'This form already exists. Please use a unique name.';

const MODAL_TITLE = 'Add Form to Library';
const MODAL_CANCEL_BUTTON_LABEL = 'Cancel';
const MODAL_SAVE_BUTTON_LABEL = 'Save';
const SAVING_LABEL = 'Saving';
const MODAL_DESCRIPTION = `Adding this form to your library means you'll be able
to use it on other events and themes in the future`;
const MODAL_NAME_INPUT_LABEL = 'Name';
const MODAL_NAME_INPUT_PLACEHOLDER = 'Enter Form Name';

const BASE_COMPONENT_NAME = 'ScaledFormsSaveFormToLibraryModal';
const MAX_ALLOWED_CHAR = 100;

const ScaledFormsSaveFormToLibraryModal = (props) => {
    const [name, setName] = useState('');

    const status = useSelector(getFormLibraryStatus);
    const error = useSelector(getFormLibraryError);

    const dispatch = useDispatch();

    const handleSave = () => {
        dispatch(addFormToLibraryEffect(name));
    };

    const handleOnChange = (e) => {
        const { value } = e.target;
        if (value.length <= MAX_ALLOWED_CHAR) {
            setName(value);
            error && dispatch(setFormLibraryError(false));
        }
    };

    useEffect(() => {
        if (status === SAVED) {
            props.onClose();
        }
    }, [status]);

    return (
        <SuiModal
            open={props.open}
            size={SuiModal.THIN}
            useClickOutside
            onRequestClose={props.onClose}
            showCloseButton={false}
        >
            <SuiContainer variation="border standard flex-vertical">
                <SuiModalHeader>
                    <SplashAppTitle size="xl" weight="headline">
                        {MODAL_TITLE}
                    </SplashAppTitle>
                </SuiModalHeader>

                <SuiModalContent>
                    <ModalContentContainer>
                        <ModalDescription>{MODAL_DESCRIPTION}</ModalDescription>
                        <SuiTextInput
                            placeholder={MODAL_NAME_INPUT_PLACEHOLDER}
                            label={MODAL_NAME_INPUT_LABEL}
                            data-qa={`${BASE_COMPONENT_NAME}__input-name`}
                            onChange={handleOnChange}
                            value={name}
                            maxLength={MAX_ALLOWED_CHAR}
                            errorText={error ? FORM_NAME_ERROR_MESSAGE : ''}
                            variation={error ? 'error' : ''}
                        />
                    </ModalContentContainer>
                </SuiModalContent>
                <SuiModalFooter>
                    <SuiContainer variation="flex-group">
                        <SuiButton
                            variation="secondary"
                            label={MODAL_CANCEL_BUTTON_LABEL}
                            onClick={props.onClose}
                            data-qa={`${BASE_COMPONENT_NAME}__button-close`}
                        />
                    </SuiContainer>
                    <SuiContainer variation="flex-group">
                        <SuiStateButton
                            label={MODAL_SAVE_BUTTON_LABEL}
                            data-qa={`${BASE_COMPONENT_NAME}__button-save`}
                            disabled={name.trim().length === 0}
                            onClick={handleSave}
                            labelLoading={SAVING_LABEL}
                            status={status === SAVING ? SuiStateButton.STATUS_LOADING : SuiStateButton.STATUS_READY}
                        />
                    </SuiContainer>
                </SuiModalFooter>
            </SuiContainer>
        </SuiModal>
    );
};

ScaledFormsSaveFormToLibraryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ScaledFormsSaveFormToLibraryModal;
